import React, { useEffect, useRef, useState } from "react"
import { useHistory } from "react-router-dom"
import moment from "moment"
import firebase from "firebase"
import { db } from "../../utils/firebase"
import "./enquiries-received.scss"

import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Button from "../../components/ui/button/button"

import DateFilter, { rangeForPreset } from "./_components/date-filter"
import Row from "./_components/row"

const HEADINGS = [
	"Enquired at",
	"Assigned to",
	"Client",
	"Group size",
	"Location/Event",
	"Travel date",
	"Age",
	"Quotes since",
	"Time to quote/outbound touch",
	"",
]

const REFRESH_MS = 30 * 1000
const CLIENT_CHUNK = 10

// Activity types that count as an agent reaching out to the client
const OUTBOUND_ACTIVITY_TYPES = ["EMAIL_SENT", "CALL_MADE", "VOICEMAIL_LEFT"]

const tsFromMillis = (millis) => firebase.firestore.Timestamp.fromMillis(millis)
const toSeconds = (ts) => ts?.seconds ?? ts?._seconds ?? null

// Split an array into chunks of size n
const chunk = (arr, n) => {
	const out = []
	for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n))
	return out
}

export default function EnquiriesReceived() {
	const history = useHistory()
	const wrapperRef = useRef(null)

	const [usersById, setUsersById] = useState({})
	const [usersLoaded, setUsersLoaded] = useState(false)

	const [preset, setPreset] = useState("TODAY")
	const [range, setRange] = useState(rangeForPreset("TODAY"))

	const [loading, setLoading] = useState(false)
	const [enquiries, setEnquiries] = useState([])
	const [clientsById, setClientsById] = useState({})
	const [quoteCounts, setQuoteCounts] = useState({})
	const [firstQuoteSecs, setFirstQuoteSecs] = useState({})
	const [firstOutboundSecs, setFirstOutboundSecs] = useState({})

	const [refreshTick, setRefreshTick] = useState(0)
	const [isFullscreen, setIsFullscreen] = useState(false)
	const [lastUpdated, setLastUpdated] = useState(null)
	const [, setDisplayTick] = useState(0)

	// If we're a "live" preset (Today / This week / This month), recompute the range on each tick so
	// it follows the moving "now". For Yesterday / Custom we leave the range fixed.
	useEffect(() => {
		const interval = setInterval(() => {
			setRefreshTick((t) => t + 1)
			if (preset === "TODAY" || preset === "THIS_WEEK" || preset === "THIS_MONTH") {
				setRange(rangeForPreset(preset))
			}
		}, REFRESH_MS)
		return () => clearInterval(interval)
	}, [preset])

	// Re-render every 5s so the "x seconds ago" label stays fresh between refresh ticks
	useEffect(() => {
		const interval = setInterval(() => setDisplayTick((t) => t + 1), 5000)
		return () => clearInterval(interval)
	}, [])

	// Track native fullscreen state changes
	useEffect(() => {
		const onChange = () => setIsFullscreen(!!document.fullscreenElement)
		document.addEventListener("fullscreenchange", onChange)
		return () => document.removeEventListener("fullscreenchange", onChange)
	}, [])

	// Load every user once on mount; small collection, used for agent name lookups
	useEffect(() => {
		db.collection("users")
			.get()
			.then((snap) => {
				const map = {}
				snap.forEach((doc) => {
					map[doc.id] = doc.data()
				})
				setUsersById(map)
				setUsersLoaded(true)
			})
	}, [])

	// Load enquiries + quotes + clients for the current range
	useEffect(() => {
		if (!range?.start || !range?.end) {
			setEnquiries([])
			setClientsById({})
			setQuoteCounts({})
			setFirstQuoteSecs({})
			return
		}

		let cancelled = false
		setLoading(true)

		const startTs = tsFromMillis(range.start)
		const endTs = tsFromMillis(range.end)

		db.collection("enquiries")
			.where("created", ">=", startTs)
			.where("created", "<=", endTs)
			.orderBy("created", "desc")
			.get()
			.then(async (enqSnap) => {
				if (cancelled) return

				const enqArr = []
				let earliestSecs = null
				enqSnap.forEach((doc) => {
					const data = doc.data()
					enqArr.push({ id: doc.id, ...data })
					const secs = toSeconds(data.created)
					if (secs !== null && (earliestSecs === null || secs < earliestSecs)) earliestSecs = secs
				})

				if (enqArr.length === 0) {
					if (cancelled) return
					setEnquiries([])
					setClientsById({})
					setQuoteCounts({})
					setFirstQuoteSecs({})
					setFirstOutboundSecs({})
					setLastUpdated(Date.now())
				setLoading(false)
					return
				}

				// Fetch quotes and clients in parallel
				const quotesPromise = db
					.collection("quotes")
					.where("created", ">=", firebase.firestore.Timestamp.fromMillis(earliestSecs * 1000))
					.get()

				const uniqueClientIds = Array.from(
					new Set(enqArr.map((e) => e.client).filter(Boolean)),
				)
				const clientChunks = chunk(uniqueClientIds, CLIENT_CHUNK)
				const clientsPromise = Promise.all(
					clientChunks.map((ids) =>
						db
							.collection("clients")
							.where(firebase.firestore.FieldPath.documentId(), "in", ids)
							.get(),
					),
				)

				// Per-enquiry activity feed: find the earliest outbound touch (email
				// sent, call made, voicemail left). Activity sub-collections are
				// small per enquiry so a parallel fan-out keeps things simple and
				// avoids needing a new collection-group index.
				const activityPromise = Promise.all(
					enqArr.map((enq) =>
						db
							.collection(`enquiries/${enq.id}/activity`)
							.orderBy("created", "asc")
							.get()
							.then((snap) => {
								let firstSecs = null
								snap.forEach((doc) => {
									if (firstSecs !== null) return
									const a = doc.data()
									if (!OUTBOUND_ACTIVITY_TYPES.includes(a.type)) return
									const secs = toSeconds(a.created)
									if (secs !== null) firstSecs = secs
								})
								return [enq.id, firstSecs]
							}),
					),
				)

				const [quotesSnap, clientSnaps, activityResults] = await Promise.all([
					quotesPromise,
					clientsPromise,
					activityPromise,
				])

				if (cancelled) return

				// Bucket quotes by client → sorted asc array of created seconds
				const quotesByClient = {}
				quotesSnap.forEach((doc) => {
					const q = doc.data()
					if (!q.client) return
					const secs = toSeconds(q.created)
					if (secs === null) return
					if (!quotesByClient[q.client]) quotesByClient[q.client] = []
					quotesByClient[q.client].push(secs)
				})
				Object.values(quotesByClient).forEach((arr) => arr.sort((a, b) => a - b))

				// Build clients lookup
				const clientsMap = {}
				clientSnaps.forEach((snap) => {
					snap.forEach((doc) => {
						clientsMap[doc.id] = doc.data()
					})
				})

				// Per-enquiry: count quotes since, and earliest quote time since
				const counts = {}
				const firsts = {}
				enqArr.forEach((enq) => {
					if (!enq.client) {
						counts[enq.id] = 0
						return
					}
					const enqSecs = toSeconds(enq.created)
					const candidates = quotesByClient[enq.client] || []
					let n = 0
					let first = null
					for (let i = 0; i < candidates.length; i++) {
						if (candidates[i] >= enqSecs) {
							n++
							if (first === null) first = candidates[i]
						}
					}
					counts[enq.id] = n
					if (first !== null) firsts[enq.id] = first
				})

				const outbounds = {}
				activityResults.forEach(([enqId, secs]) => {
					if (secs !== null) outbounds[enqId] = secs
				})

				setEnquiries(enqArr)
				setClientsById(clientsMap)
				setQuoteCounts(counts)
				setFirstQuoteSecs(firsts)
				setFirstOutboundSecs(outbounds)
				setLastUpdated(Date.now())
				setLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [range?.start, range?.end, refreshTick])

	const handleRangeChange = ({ preset: nextPreset, start, end }) => {
		setPreset(nextPreset)
		if (start && end) {
			setRange({ start, end })
		} else {
			setRange(null)
			setEnquiries([])
			setClientsById({})
			setQuoteCounts({})
			setFirstQuoteSecs({})
			setFirstOutboundSecs({})
		}
	}

	const toggleFullscreen = () => {
		if (!document.fullscreenElement) {
			wrapperRef.current?.requestFullscreen?.()
		} else {
			document.exitFullscreen?.()
		}
	}

	return (
		<div
			ref={wrapperRef}
			className={`enquiries-received-wrapper ${isFullscreen ? "enquiries-received-fullscreen" : ""}`.trim()}>
			<Tile fullPage={true}>
				<Title>
					<h1>Quoted Enquiries</h1>
				</Title>

				<div className="page-filters">
					<DateFilter
						preset={preset}
						onChange={handleRangeChange}
					/>
					<Button
						small={true}
						label={isFullscreen ? "Exit full screen" : "Full screen"}
						onClick={toggleFullscreen}
					/>
					{lastUpdated && (
						<span className="last-updated">
							Updated {moment(lastUpdated).fromNow()}
						</span>
					)}
				</div>

				<Table
					className="enquiries-table"
					headings={HEADINGS}
					noResults={!loading && enquiries.length === 0}
					noResultsMessage={
						!range
							? "Choose a custom date range to see enquiries"
							: "No enquiries found for this date range"
					}>
					{usersLoaded &&
						enquiries.map((enq) => (
							<Row
								key={enq.id}
								enquiry={enq}
								usersById={usersById}
								client={enq.client ? clientsById[enq.client] : null}
								quoteCount={quoteCounts[enq.id] || 0}
								firstQuoteSecs={firstQuoteSecs[enq.id] || null}
								firstOutboundSecs={firstOutboundSecs[enq.id] || null}
								onView={(id) => history.push(`/enquiry/${id}`)}
							/>
						))}
				</Table>
			</Tile>
		</div>
	)
}
